import Box from "@mui/material/Box"
import FormHelperText from "@mui/material/FormHelperText"
import InputLabel from "@mui/material/InputLabel"
import Typography from "@mui/material/Typography"
import { useId, useMemo, useState } from "react"
import { Controller, type Control, type FieldValues } from "react-hook-form"
import type {
  GroupBase,
  MultiValue,
  OptionsOrGroups,
  SingleValue
} from "react-select"
import { AsyncPaginate } from "react-select-async-paginate"

import { INPUT_LABEL_BLUR, PRIMARY } from "@constants/colors"
import { Country, StateProvinceCity } from "__generated__/graphql"

type SelectedLocation = Country & StateProvinceCity & { value?: string }

import { OptionType } from "@components/dialogs/create_coupon"
import styles from "./styles"

type MultiSelectAsyncInputProps<T extends FieldValues> = {
  name: keyof T
  control: Control<T>
  selectDefaultValue?: { value: string; label: string }
  label: string
  placeholder: string
  isLoading?: boolean
  disabled?: boolean
  isMulti?: boolean
  closeMenuOnSelect?: boolean
  isOptionDisabled?: (option: { value: string; label: string }) => boolean
  maxMenuHeight?: number
  required?: boolean
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  loadOptions: (
    search: string,
    prevOptions: OptionsOrGroups<OptionType, GroupBase<OptionType>>
  ) => Promise<{
    options: { value: string; label: string }[]
    hasMore: boolean
  }>
  defaultValue?: { value: string; label: string }[]
  value?: { value: string; label: string }
  setSelectedCountry?: (selected: Country) => void
  setSelectedProvince?: (selected: StateProvinceCity) => void
  setSelectedCity?: (selected: StateProvinceCity) => void
  onMenuClose?: () => void
  cacheUniqs?: string[]
  getSelectedOption?: (selected: SelectedLocation) => void
}

export default function MultiSelectAsync<T extends FieldValues>(
  props: MultiSelectAsyncInputProps<T>
) {
  const [isFocus, setIsFocus] = useState(false)
  const componentId = useId()
  function onFocus(event: React.FocusEvent<HTMLInputElement>) {
    setIsFocus(true)
    props?.onFocus && props?.onFocus(event)
  }

  function onBlur(event: React.FocusEvent<HTMLInputElement>) {
    setIsFocus(false)
    props?.onBlur && props?.onBlur(event)
  }

  const defaultValue = useMemo(() => {
    if (
      props.isMulti === false &&
      props.defaultValue &&
      props.defaultValue.length > 0
    ) {
      return props.defaultValue[0]
    }
    return props.defaultValue
  }, [props.isMulti, props.defaultValue])
  return (
    <Box sx={styles.container}>
      <Controller<FieldValues>
        name={props.name.toString()}
        control={props.control as unknown as Control<FieldValues>}
        render={({ field: { onChange }, fieldState: { error } }) => (
          <>
            <InputLabel
              sx={{
                ...styles.labelContainer,
                color: isFocus ? PRIMARY : INPUT_LABEL_BLUR
              }}
              error={!!error}
              htmlFor={componentId}
              required={props?.required}
            >
              <Typography>{props.label}</Typography>
            </InputLabel>
            <AsyncPaginate
              isSearchable
              loadOptions={props.loadOptions}
              isMulti={props.isMulti === undefined ? true : props.isMulti}
              isDisabled={props.disabled}
              placeholder={props.placeholder}
              isLoading={props.isLoading}
              cacheUniqs={props.cacheUniqs}
              closeMenuOnSelect={props.closeMenuOnSelect}
              onMenuClose={props.onMenuClose}
              defaultValue={defaultValue}
              value={props.value}
              menuPosition="fixed"
              onChange={(
                selectedOptions:
                  | MultiValue<{ value: string; label: string }>
                  | SingleValue<{ value: string; label: string }>
                  | null
              ) => {
                props.getSelectedOption &&
                  props.getSelectedOption(
                    selectedOptions as unknown as SelectedLocation
                  )
                // props.setSelectedCountry &&
                //   props.setSelectedCountry(selectedOptions as Country)
                // props.setSelectedProvince &&
                //   props.setSelectedProvince(
                //     selectedOptions as StateProvinceCity
                //   )
                props.setSelectedCity &&
                  props.setSelectedCity(selectedOptions as StateProvinceCity)
                if (selectedOptions && Array.isArray(selectedOptions)) {
                  onChange(
                    selectedOptions.map(
                      (option: { value: string; label: string }) => option.value
                    )
                  )
                } else if (selectedOptions && !Array.isArray(selectedOptions)) {
                  const option: { value: string; label: string } =
                    selectedOptions as { value: string; label: string }
                  onChange(option.value)
                }
              }}
              onBlur={onBlur}
              isOptionDisabled={props.isOptionDisabled}
              maxMenuHeight={props.maxMenuHeight}
              onFocus={onFocus}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  padding: "0.211rem"
                }),
                menu: (baseStyles) => ({
                  ...baseStyles,
                  marginTop: 0
                })
              }}
            />
            <FormHelperText
              error={!!error}
              sx={{ display: !!error ? "block" : "none" }}
            >
              {error ? error?.message : <></>}
            </FormHelperText>
          </>
        )}
      />
    </Box>
  )
}
